import { useCallback, useEffect, useRef } from 'react';
import type { NarrationBlockScroller, NarrationScrollHandle } from '../narrationScroll';

export type NarrationAvailability = (
  blockId: string,
  onReady: () => void,
  onInterrupted?: () => void,
) => () => void;

export interface NarrationScrollCoordinator {
  scrollBlockWhenAvailable: (blockId: string, onSettled: () => void, onInterrupted?: () => void) => void;
  cancelActiveScroll: () => void;
}

/** Wait for pacing and mounting explicitly; absence never becomes permission to play. */
export function useNarrationScrollCoordinator(
  scrollBlockIntoView: NarrationBlockScroller,
  prepareBlock?: NarrationAvailability,
): NarrationScrollCoordinator {
  const activeScrollRef = useRef<NarrationScrollHandle | null>(null);
  const cancelWaitRef = useRef<(() => void) | null>(null);
  const mountObserverRef = useRef<MutationObserver | null>(null);
  const generation = useRef(0);
  const inputs = useRef({ scrollBlockIntoView, prepareBlock });
  inputs.current = { scrollBlockIntoView, prepareBlock };

  const cancelActiveScroll = useCallback((): void => {
    generation.current += 1;
    cancelWaitRef.current?.();
    cancelWaitRef.current = null;
    mountObserverRef.current?.disconnect();
    mountObserverRef.current = null;
    activeScrollRef.current?.cancel();
    activeScrollRef.current = null;
  }, []);

  const scrollBlockWhenAvailable = useCallback((blockId: string, onSettled: () => void, onInterrupted?: () => void): void => {
    cancelActiveScroll();
    const token = generation.current;
    const interrupt = (): void => {
      if (token !== generation.current) return;
      cancelActiveScroll();
      onInterrupted?.();
    };
    const position = (): void => {
      if (token !== generation.current) return;
      let settled = false;
      const handle = inputs.current.scrollBlockIntoView(blockId, () => {
        settled = true;
        if (token !== generation.current) return;
        activeScrollRef.current = null;
        mountObserverRef.current?.disconnect();
        mountObserverRef.current = null;
        onSettled();
      }, interrupt);
      if (token !== generation.current) {
        handle?.cancel();
        return;
      }
      if (handle) {
        mountObserverRef.current?.disconnect();
        mountObserverRef.current = null;
        if (!settled) activeScrollRef.current = handle;
      } else if (!mountObserverRef.current) {
        mountObserverRef.current = new MutationObserver(position);
        mountObserverRef.current.observe(document.body, { childList: true, subtree: true });
      }
    };
    const prepare = inputs.current.prepareBlock;
    if (prepare) {
      const cancel = prepare(blockId, position, interrupt);
      if (token === generation.current) cancelWaitRef.current = cancel;
      else cancel();
    } else position();
  }, [cancelActiveScroll]);

  useEffect(() => () => cancelActiveScroll(), [cancelActiveScroll]);
  return { scrollBlockWhenAvailable, cancelActiveScroll };
}
